import { useState } from "react";
import { motion } from "framer-motion";
import {
  Bell, Search, Sparkles, Send, MapPin, Users, Calendar, Tag,
  CalendarClock, Compass, Shield, Trophy, Flame, ChevronRight, Navigation,
} from "lucide-react";

// ── Standalone DARK-THEME prototype (TAMU maroon = worst-case legibility test).
// Does NOT touch the live app. Route: /proto/dark
// Tenant brand is injected as a CSS var; on-dark accent is derived with color-mix.

const EVENTS = [
  {
    title: "Campus Concert", time: "Tonight · 7PM", place: "SUB Courtyard",
    img: "https://images.unsplash.com/photo-1622604647545-0cada2f34470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjb2xsZWdlJTIwY2FtcHVzJTIwZXZlbnQlMjBjcm93ZHxlbnwwfHx8fDE3ODU5NzIyNDJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Coffee & Connections", time: "Tomorrow · 10AM", place: "Evans Library",
    img: "https://images.unsplash.com/photo-1693608231470-25e1b16a23b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxjb2xsZWdlJTIwY2FtcHVzJTIwZXZlbnQlMjBjcm93ZHxlbnwwfHx8fDE3ODU5NzIyNDJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Intramural Basketball", time: "Fri · 6PM", place: "Rec Center",
    img: "https://images.unsplash.com/photo-1608453162650-cba45689c284?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMGhhcHB5JTIwY29sbGVnZSUyMHN0dWRlbnQlMjBmYWNlfGVufDB8fHx8MTc4NTk3MjI0Mnww&ixlib=rb-4.1.0&q=85",
  },
];

const CREW = [
  "https://images.unsplash.com/photo-1759852692971-a2abc6799cbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHx5b3VuZyUyMGhhcHB5JTIwY29sbGVnZSUyMHN0dWRlbnQlMjBmYWNlfGVufDB8fHx8MTc4NTk3MjI0Mnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1608453162650-cba45689c284?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMGhhcHB5JTIwY29sbGVnZSUyMHN0dWRlbnQlMjBmYWNlfGVufDB8fHx8MTc4NTk3MjI0Mnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1544168190-79c17527004f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHx5b3VuZyUyMGhhcHB5JTIwY29sbGVnZSUyMHN0dWRlbnQlMjBmYWNlfGVufDB8fHx8MTc4NTk3MjI0Mnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1604177091072-b7b677a077f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMGhhcHB5JTIwY29sbGVnZSUyMHN0dWRlbnQlMjBmYWNlfGVufDB8fHx8MTc4NTk3MjI0Mnww&ixlib=rb-4.1.0&q=85",
];

const EXPLORE = [
  { label: "Events", icon: Calendar },
  { label: "Groups", icon: Users },
  { label: "Places", icon: MapPin },
  { label: "Deals", icon: Tag },
];

const NAV = [
  { label: "Today", icon: CalendarClock, active: true },
  { label: "Places", icon: Compass },
  { label: "Friends", icon: Users },
  { label: "Walk", icon: Shield },
  { label: "Rewards", icon: Trophy },
];

const CHIPS = ["30 min", "1 hour", "2 hours", "Tonight"];

function Section({ title, action, children, testid }) {
  return (
    <section className="mb-7" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="font-display text-lg font-semibold tracking-tight text-white">{title}</h2>
        {action && <button className="text-xs font-semibold text-[var(--brand-on-dark)]">{action}</button>}
      </div>
      {children}
    </section>
  );
}

export default function ProtoDark() {
  const [chip, setChip] = useState(null);
  // TAMU maroon injected as the tenant brand; on-dark variant derived via color-mix.
  const style = {
    "--brand": "#500000",
    "--brand-on-dark": "color-mix(in oklab, #500000, white 65%)",
    "--brand-surface": "color-mix(in oklab, #500000, transparent 82%)",
  };

  return (
    <div
      data-testid="proto-dark-root"
      style={style}
      className="min-h-screen bg-[#09090b] text-white antialiased mx-auto max-w-md relative"
    >
      {/* Ambient brand glow */}
      <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full blur-3xl"
        style={{ background: "var(--brand-surface)" }} />

      {/* Sticky glass header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-zinc-950/70 border-b border-white/10">
        <div className="flex items-center justify-between px-5 h-16">
          <img src="/jeeo-logo-trans.png" alt="jeeo" className="h-8 w-auto" />
          <div className="flex items-center gap-3">
            <button className="relative w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors duration-200" data-testid="proto-bell">
              <Bell size={16} className="text-zinc-300" />
              <span className="absolute top-2 right-2.5 w-1.5 h-1.5 rounded-full bg-[#37C9E0]" />
            </button>
            {/* School crest on a LIGHT chip → crisp on dark regardless of crest colors */}
            <div className="w-9 h-9 rounded-full bg-white p-1 shadow-md shadow-black/50 flex items-center justify-center" data-testid="proto-crest-chip">
              <img src="/tamu-emblem.png" alt="Texas A&M" className="w-full h-full object-contain rounded-full" />
            </div>
          </div>
        </div>
      </header>

      <div className="px-5 pt-5 pb-28 relative z-10">
        {/* Greeting */}
        <div className="mb-1">
          <div className="mono text-[11px] tracking-[0.2em] uppercase mb-1" style={{ color: "var(--brand-on-dark)" }}>
            Texas A&amp;M University
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight leading-tight">
            Welcome to<br />your campus!
          </h1>
        </div>

        {/* Search pill */}
        <div className="mt-4 mb-6 flex items-center gap-3 rounded-full bg-zinc-800/50 border border-zinc-700 px-4 py-3" data-testid="proto-search">
          <Search size={17} className="text-zinc-400 shrink-0" />
          <span className="text-sm text-zinc-400">Find events, people, or places</span>
        </div>

        {/* AI gap-time companion */}
        <div className="mb-7 rounded-3xl p-[1.5px] bg-gradient-to-br from-[#FFD21E]/60 via-[#37C9E0]/40 to-transparent" data-testid="proto-companion">
          <div className="rounded-3xl bg-zinc-900/90 p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-xl bg-[#FFD21E]/15 text-[#FFD21E] flex items-center justify-center">
                <Sparkles size={17} />
              </span>
              <div>
                <div className="font-semibold text-sm">What should I do?</div>
                <div className="text-xs text-zinc-400">Ask jeeo how to spend your free time</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center rounded-xl bg-zinc-800/70 border border-zinc-700 px-3 py-2.5 text-sm text-zinc-400">
                I have 2 hours free before my next class
              </div>
              <button className="w-10 h-10 rounded-xl text-black flex items-center justify-center shrink-0" style={{ backgroundColor: "#FFD21E" }}>
                <Send size={16} />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {CHIPS.map((c) => (
                <button
                  key={c}
                  onClick={() => setChip(c)}
                  data-testid={`proto-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors duration-200"
                  style={chip === c
                    ? { backgroundColor: "var(--brand-on-dark)", color: "#09090b", borderColor: "transparent" }
                    : { borderColor: "rgba(255,255,255,0.15)", color: "#d4d4d8" }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* What's happening — photo cards */}
        <Section title="What's happening" action="See all" testid="proto-happening">
          <div className="flex gap-4 overflow-x-auto hide-scrollbar -mx-5 px-5 pb-1">
            {EVENTS.map((e) => (
              <motion.div
                key={e.title}
                whileHover={{ y: -3 }}
                className="relative shrink-0 w-44 h-56 rounded-2xl overflow-hidden border border-white/10 shadow-xl shadow-black/50"
                data-testid={`proto-event-${e.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <img src={e.img} alt={e.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="font-semibold text-sm leading-tight">{e.title}</div>
                  <div className="text-[11px] text-zinc-300 mt-1">{e.time}</div>
                  <div className="text-[11px] text-zinc-400">{e.place}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* Your crew */}
        <Section title="Your crew" testid="proto-crew">
          <div className="rounded-2xl bg-zinc-900/70 border border-white/10 p-4 flex items-center gap-4">
            <div className="flex -space-x-4 shrink-0">
              {CREW.map((src, i) => (
                <div key={i} className="relative">
                  <img src={src} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-zinc-950" />
                  {i < 2 && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#37C9E0] border-2 border-zinc-950" />}
                </div>
              ))}
              <div className="w-11 h-11 rounded-full bg-zinc-800 border-2 border-zinc-950 text-xs font-bold flex items-center justify-center">+3</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm">2 friends free now</div>
              <div className="text-xs text-zinc-400">See who's free and where to meet up</div>
            </div>
            <ChevronRight size={18} className="text-zinc-500 shrink-0" />
          </div>
        </Section>

        {/* Explore your campus */}
        <Section title="Explore your campus" testid="proto-explore">
          <div className="grid grid-cols-4 gap-3">
            {EXPLORE.map((t) => (
              <button
                key={t.label}
                data-testid={`proto-explore-${t.label.toLowerCase()}`}
                className="rounded-2xl bg-zinc-900/70 border border-white/10 py-4 flex flex-col items-center gap-2 transition-colors duration-200 hover:border-white/25"
                style={{ ["--hover"]: "var(--brand-surface)" }}
              >
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "var(--brand-surface)", color: "var(--brand-on-dark)" }}>
                  <t.icon size={18} />
                </span>
                <span className="text-[11px] font-semibold text-zinc-300">{t.label}</span>
              </button>
            ))}
          </div>
        </Section>

        {/* Fill your gaps callout (brand-on-dank legibility showcase) */}
        <Section title="Fill your gaps" testid="proto-gaps">
          <button className="w-full rounded-2xl p-4 flex items-center gap-3 border transition-colors duration-200"
            style={{ backgroundColor: "var(--brand-surface)", borderColor: "var(--brand-on-dark)" }}>
            <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0" style={{ color: "var(--brand-on-dark)" }}>
              <Navigation size={18} />
            </span>
            <div className="min-w-0 flex-1 text-left">
              <div className="font-semibold text-sm" style={{ color: "var(--brand-on-dark)" }}>90 min gap after CSCE 221</div>
              <div className="text-xs text-zinc-400">Grab lunch at Sbisa · 8 min walk</div>
            </div>
            <ChevronRight size={18} style={{ color: "var(--brand-on-dark)" }} className="shrink-0" />
          </button>
        </Section>
      </div>

      {/* Glass bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 mx-auto max-w-md backdrop-blur-2xl bg-zinc-950/80 border-t border-white/10" data-testid="proto-bottom-nav">
        <div className="flex">
          {NAV.map((n) => (
            <button
              key={n.label}
              data-testid={`proto-nav-${n.label.toLowerCase()}`}
              className="flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-semibold"
              style={{ color: n.active ? "#FFD21E" : "#71717a" }}
            >
              <n.icon size={20} />
              {n.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
